import { SearchOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Drawer, Space, Input, Table, Button } from 'antd';
import { isEmpty, isEqual } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import React, { FC, useState, useEffect, useMemo } from 'react';
import { formatTimestamp } from '@/infrastructure/utils/timestamp';
import { ConfigTabType, showHistory } from '@/presentation/pages/setting/app-deliver-config/api';
import { RecordType } from '@/presentation/pages/setting/app-deliver-config/components/configuration-content';
import { getForm } from '@/presentation/pages/setting/app-deliver-config/config-modal/edit';
import { DiffModal } from '@/presentation/pages/setting/app-deliver-config/config-modal/edit/diff-modal';
import { ViewConfigModal } from '@/presentation/pages/setting/app-deliver-config/config-modal/view';
import { transform2Form } from '@/presentation/pages/setting/app-deliver-config/utils';
import { useStores } from '@/presentation/store';

interface IProps {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  tab: ConfigTabType;
}

export const HistoryConfigModal: FC<IProps> = observer((props) => {
  const { data: initData, loading } = useRequest(() => showHistory(props.tab), {
    refreshDeps: [props.visible],
    ready: props.visible,
  });
  const [diffVisible, setDiffVisible] = useState<boolean>(false);
  const [viewVisible, setViewVisible] = useState<boolean>(false);
  const [key, setKey] = useState('');
  const [data, setData] = useState(initData);
  const [viewConfig, setViewConfig] = useState();
  const { androidFullConfig = {}, iosFullConfig = {}, flutterFullConfig = {},
    windowsFullConfig = {}, harmonyOSFullConfig = {}, configs: operation = {} } = useStores().ConfigStore;
  const { configMode = {} } = operation;
  const fullConfigMode = {
    ...configMode.android,
    ...configMode.ios,
    ...configMode.flutter,
  };

  useEffect(() => {
    setData(initData);
  }, [initData]);

  const renderAction = (_: unknown, record: RecordType) => {
    const { after, before, defaultBefore, defaultAfter } = record;
    const afterEnable = after?.enabled;
    const beforeEnable = before?.enabled;
    const isDiffEnable = !isEqual(afterEnable, beforeEnable);
    const isEmptyAfter = isEmpty(JSON.parse(defaultAfter || '{}'));
    const isEmptyBefore = isEmpty(JSON.parse(defaultBefore || '{}'));
    if (isEmptyAfter) {
      return <Button type={'link'} style={{ paddingLeft: 0 }} onClick={() => {
        setViewVisible(true);
        setViewConfig({
          ...before,
          createTime: moment(before.createTime).unix(),
          updateTime: moment(before.updateTime).unix(),
        });
      }}>删除配置任务</Button>;
    }
    if (isEmptyBefore) {
      return <Button type={'link'} style={{ paddingLeft: 0 }} onClick={() => {
        setViewVisible(true);
        setViewConfig({
          ...after,
          createTime: moment(after.createTime).unix(),
          updateTime: moment(after.updateTime).unix(),
        });
      }}>新建配置任务</Button>;
    }
    if (isDiffEnable) {
      return <span style={{ color: '#7B889C' }}>{!!afterEnable ? '开启配置任务' : '关闭配置任务' }</span>;
    }
    return <Button type={'link'} style={{ paddingLeft: 0 }} onClick={() => {
      setDiffVisible(true);
      setKey(record.key);
    }}>修改配置任务</Button>;
  };

  const columns = [
    {
      title: '修改时间',
      dataIndex: 'updateTime',
      width: 120,
      render: (data: string) => formatTimestamp(data, 'YYYY-MM-DD HH:mm'),
    },
    {
      title: '修改人',
      dataIndex: 'updateUser',
      render: (data: string) => data || '-',
      width: 120,
    },
    {
      title: '配置任务（任务ID）',
      render: (_: unknown, record: RecordType) => `${record.after.name || record.before.name || '-'}（id:${record.after.id || record.before.id || '-'}）`,
    },
    {
      title: '操作',
      render: renderAction,
    },
  ];
  const keyData = useMemo(() => data?.find(item => item.key === key), [data, key]);
  const getNextCode = () => {
    const {
      chunkConfig,
      sampleRatio,
    } = transform2Form(
      keyData?.after.content || '{}', androidFullConfig, iosFullConfig, flutterFullConfig,
      windowsFullConfig, harmonyOSFullConfig, fullConfigMode,
    );
    return JSON.stringify({
      configs: chunkConfig,
      sampleRatio,
    });
  };

  const onChange = (e: any) => {
    const { value } = e.target;
    if (!!value) {
      const data = initData?.filter(item => item?.after.name?.match(new RegExp(value, 'i'))
        || item.after.updateUser?.match(new RegExp(value, 'i')));
      setData(data);
    } else {
      setData(initData);
    }
  };

  return <Drawer
    title={'配置任务修改历史'}
    width={700}
    visible={props.visible}
    onClose={() => props.setVisible(false)}
    destroyOnClose
  >
    <Space direction={'vertical'} style={{ width: '100%' }} size={'middle'}>
      <span style={{ color: '#4A5970' }}>只支持查看2023年4月11日之后最近两年的修改历史</span>
      <Input
        placeholder={'请搜索配置任务或最后修改人'}
        style={{ width: 320 }}
        onChange={onChange}
        suffix={<SearchOutlined style={{ color: '#4A5970' }} />}
      />
      <Table
        rowKey={'key'}
        loading={loading}
        columns={columns}
        dataSource={data!}
        pagination={{
          showSizeChanger: true,
          defaultCurrent: 1,
          defaultPageSize: 20,
          total: data?.length,
        }}
      />
      <DiffModal
        prevForm={getForm(keyData?.before)}
        nextForm={getForm(keyData?.after)}
        prevCode={keyData?.before.content || '{}'}
        nextCode={getNextCode()}
        visible={diffVisible}
        onSubmit={() => {}}
        onHide={() => setDiffVisible(false)}
        type={'view'}
        tab={props.tab}
      />
      <ViewConfigModal
        visible={viewVisible}
        tab={props.tab}
        onOk={() => {}}
        onCancel={() => setViewVisible(false)}
        values={viewConfig}
      />
    </Space>
  </Drawer>;
});
